import { ActionIcon, Button, Grid, Group, Select, Stack, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';
import ISO6391 from 'iso-639-1';
import { useMemo, useState } from 'react';
import { appendTag } from '../../helpers/chatBotMessageTags';
import AdjustableTextArea from '../GeneratedContent/AdjustableTextArea';
import InitialMessageTagBuilder from './InitialMessageTagBuilder';

const DEFAULT_KEY = 'default';
const ADDABLE_LANGUAGES = ISO6391.getLanguages(ISO6391.getAllCodes()).map((locale) => ({
  value: locale.code,
  label: locale.name,
}));

const labelFor = (code) => {
  if (code === DEFAULT_KEY) return 'Default / Fallback';
  const name = ISO6391.getName(code);
  return name || code.toUpperCase();
};

const InitialMessagesEditor = ({
  value,
  onChange,
  domainLocale,
  singleLine = false,
  idPrefix = 'initialMessage',
  placeholder = '',
  withTagBuilder = false,
  productFeeds = [],
  agentUi = null,
}) => {
  const messages = value || {};

  const orderedKeys = useMemo(() => {
    const keys = Object.keys(messages);
    const rest = keys.filter((k) => k !== DEFAULT_KEY && k !== domainLocale);
    rest.sort((a, b) => labelFor(a).localeCompare(labelFor(b)));

    const ordered = [];
    if (domainLocale && messages[domainLocale] !== undefined) ordered.push(domainLocale);
    if (messages[DEFAULT_KEY] !== undefined) ordered.push(DEFAULT_KEY);
    ordered.push(...rest);
    return ordered;
  }, [messages, domainLocale]);

  const availableToAdd = useMemo(
    () =>
      ADDABLE_LANGUAGES.filter(
        (lang) => messages[lang.value] === undefined && lang.value !== domainLocale
      ),
    [messages, domainLocale]
  );

  const [pendingLang, setPendingLang] = useState(null);

  const updateKey = (key, text) => {
    onChange({ ...messages, [key]: text });
  };

  const removeKey = (key) => {
    const next = { ...messages };
    delete next[key];
    onChange(next);
  };

  const addPending = () => {
    if (!pendingLang || messages[pendingLang] !== undefined) return;
    onChange({ ...messages, [pendingLang]: '' });
    setPendingLang(null);
  };

  const canRemove = (key) => key !== DEFAULT_KEY && key !== domainLocale;

  return (
    <Stack gap="sm">
      {orderedKeys.map((key) => {
        // For non-default rows, hint that an empty value falls back to the default text.
        const rowPlaceholder = key !== DEFAULT_KEY && messages[DEFAULT_KEY] ? messages[DEFAULT_KEY] : placeholder;
        return (
          <div key={key}>
            <Group justify="space-between" mb={4}>
              <Text size="sm" fw={500}>
                {labelFor(key)}
                {key === domainLocale && (
                  <Text component="span" size="xs" c="dimmed" ml={6}>
                    (domain language)
                  </Text>
                )}
              </Text>
              {canRemove(key) && (
                <ActionIcon
                  variant="subtle"
                  color="red"
                  size="sm"
                  aria-label={`Remove ${labelFor(key)}`}
                  onClick={() => removeKey(key)}
                >
                  <IconTrash size={14} />
                </ActionIcon>
              )}
            </Group>
            {singleLine ? (
              <TextInput
                id={`${idPrefix}-${key}`}
                name={`${idPrefix}-${key}`}
                placeholder={rowPlaceholder}
                value={messages[key] || ''}
                onChange={(e) => updateKey(key, e.target.value)}
              />
            ) : (
              <AdjustableTextArea
                id={`${idPrefix}-${key}`}
                name={`${idPrefix}-${key}`}
                placeholder={rowPlaceholder}
                value={messages[key] || ''}
                onChange={(e) => updateKey(key, e.target.value)}
              />
            )}
            {withTagBuilder && !singleLine && (
              <InitialMessageTagBuilder
                productFeeds={productFeeds}
                agentUi={agentUi}
                locale={key !== DEFAULT_KEY ? key : domainLocale}
                onInsert={(tag) => updateKey(key, appendTag(messages[key], tag))}
              />
            )}
          </div>
        );
      })}

      <Grid align="end" gutter="xs" mt="xs">
        <Grid.Col span={{ base: 12, sm: 8 }}>
          <Select
            data={availableToAdd}
            value={pendingLang}
            onChange={setPendingLang}
            placeholder={availableToAdd.length === 0 ? 'No languages available' : 'Select a language'}
            disabled={availableToAdd.length === 0}
            searchable
          />
        </Grid.Col>
        <Grid.Col span={{ base: 12, sm: 4 }}>
          <Button
            fullWidth
            variant="default"
            size="sm"
            onClick={addPending}
            disabled={!pendingLang || availableToAdd.length === 0}
          >
            <IconPlus size={16} /> Add Language
          </Button>
        </Grid.Col>
      </Grid>
    </Stack>
  );
};

export default InitialMessagesEditor;
